/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DropdownSearchBar from "../dorpdown_searchbar/index.jsx";
import { LoadingSpinner } from "../loading_spinner";
import { ModalCancelButton } from "../modal_cancel_button/index";
import { SelectedMenuItem } from "../selected_menu_item/index.jsx";
import { StyledMenuItem } from "../styled_menu_item/index.jsx";
import { StyledSelect } from "../../pages/network_guests/styles.jsx";
import { matchesNormalizedSearch } from "../../utils/normalizeSearchTerm";
import { DropdownInfiniteLoader } from "../dropdown_infinite_loader";
import { useInfiniteScrollObserver } from "../../hooks/useInfiniteScrollObserver";
import { mergeUniqueByKey } from "../../utils/dropdownInfiniteQuery";
import ColumnMappingStep from "./column_mapping_step.jsx";
import ImportSummaryStep from "./import_summary_step.jsx";
import UploadStep from "./upload_step.jsx";
import {
  BodyContainer,
  FooterActions,
  FooterContainer,
  FooterDivider,
  FooterLeft,
  HeaderContainer,
  HeaderTopRow,
  ModalContainer,
  PrimaryButton,
  SecondaryButton,
  StepCircle,
  StepConnector,
  StepItem,
  StepLabel,
  StepperContainer,
  StyledCloseButton,
  SubtitleText,
  TitleText,
} from "./styles.jsx";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const MAX_DATA_ROWS = 500;
const STEP_LABELS = ["Upload", "Map columns", "Import Summary"];
const REQUIRED_FIELDS = ["first_name", "last_name", "phone_number", "job_title"];
const FIELD_OPTIONS = [
  { label: "First Name", value: "first_name", required: true },
  { label: "Last Name", value: "last_name", required: true },
  { label: "Phone Number", value: "phone_number", required: true },
  { label: "Job Title", value: "job_title", required: true },
  { label: "Email", value: "email", required: false },
  { label: "Date of birth", value: "date_of_birth", required: false },
  { label: "Wage", value: "wage", required: false },
];
const SAMPLE_VALUES = {
  first_name: "John - Marry - Tom",
  last_name: "Doe - Joshnson - Will",
  phone_number: "9876543210 - 9978654321 - 8856489076",
  job_title: "Cook - Busser - Dishwasher",
  date_of_birth: "2000/01/01 - 2001/06/06 - 2002/11/16",
  email: "john@example.com - marry@example.com",
  wage: "10 - 33 - 57",
};
const HEADER_ALIAS_MAP = {
  first_name: "first_name",
  firstname: "first_name",
  name: "first_name",
  last_name: "last_name",
  lastname: "last_name",
  surname: "last_name",
  phone: "phone_number",
  phone_number: "phone_number",
  mobile: "phone_number",
  mobile_number: "phone_number",
  job_title: "job_title",
  experience: "job_title",
  email: "email",
  mail: "email",
  date_of_birth: "date_of_birth",
  dob: "date_of_birth",
  wage: "wage",
  salary: "wage",
  pay_rate: "wage",
};

const normalizeCsvHeader = (value) => value
  ?.toString()
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "_")
  .replace(/^_+|_+$/g, "") || "";

const parseCsvText = (text) => {
  const rows = [];
  let currentRow = [];
  let currentValue = "";
  let inQuotes = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    const nextCharacter = text[index + 1];

    if (character === '"') {
      if (inQuotes && nextCharacter === '"') {
        currentValue += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (character === "," && !inQuotes) {
      currentRow.push(currentValue);
      currentValue = "";
      continue;
    }

    if ((character === "\n" || character === "\r") && !inQuotes) {
      if (character === "\r" && nextCharacter === "\n") {
        index += 1;
      }

      currentRow.push(currentValue);
      rows.push(currentRow);
      currentRow = [];
      currentValue = "";
      continue;
    }

    currentValue += character;
  }

  currentRow.push(currentValue);
  rows.push(currentRow);

  return rows;
};

const serializeCsvValue = (value) => {
  if (value == null) {
    return "";
  }

  const stringValue = `${value}`;
  if (/[",\n\r]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const serializeCsv = (rows) => rows.map((row) => row.map(serializeCsvValue).join(",")).join("\r\n");

const buildInitialMappings = (headers) => {
  const usedValues = new Set();

  return headers.reduce((accumulator, header) => {
    const mappedValue = HEADER_ALIAS_MAP[header.normalizedHeader];
    if (!mappedValue || usedValues.has(mappedValue)) {
      return accumulator;
    }

    accumulator[header.normalizedHeader] = mappedValue;
    usedValues.add(mappedValue);
    return accumulator;
  }, {});
};

const preprocessCsvFile = async (file) => {
  const text = await file.text();
  const parsedRows = parseCsvText(text)
    .map((row) => row.map((value) => value?.replace(/^\uFEFF/, "")))
    .filter((row, index) => index === 0 || row.some((cell) => `${cell || ""}`.trim() !== ""));

  if (parsedRows.length === 0 || parsedRows[0].every((cell) => `${cell || ""}`.trim() === "")) {
    throw new Error("CSV file is empty");
  }

  const headers = parsedRows[0].map((header) => `${header || ""}`.trim());
  const normalizedHeaders = headers.map((header) => normalizeCsvHeader(header));

  if (normalizedHeaders.some((header) => !header)) {
    throw new Error("CSV headers cannot be blank");
  }

  if (new Set(normalizedHeaders).size !== normalizedHeaders.length) {
    throw new Error("CSV headers must be unique after normalization");
  }

  const dataRows = parsedRows.slice(1);

  if (dataRows.length > MAX_DATA_ROWS) {
    throw new Error(`Uploaded CSV has more than ${MAX_DATA_ROWS} rows. CSV should contain ${MAX_DATA_ROWS} rows or fewer, excluding the header row`);
  }

  const processedFile = new File([
    serializeCsv([normalizedHeaders, ...dataRows]),
  ], file.name, { type: "text/csv" });

  return {
    processedFile,
    fileName: file.name,
    rowCount: dataRows.length,
    columnCount: headers.length,
    headers: headers.map((originalHeader, index) => ({
      originalHeader,
      normalizedHeader: normalizedHeaders[index],
    })),
  };
};

export default function ManualCsvUploadEmployee({
  open,
  onClose,
  onImport,
  restaurants = [],
  restaurantOptionsCache = [],
  restaurantSearch = "",
  onRestaurantSearchChange = () => {},
  fetchNextRestaurantsPage,
  hasNextRestaurantsPage = false,
  restaurantsFetchingNextPage = false,
  isUploading = false,
  createRestaurantFetching = false,
  disableRestaurantFilter = false,
  defaultRestaurantId = "",
}) {
  const [step, setStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [processedFile, setProcessedFile] = useState(null);
  const [csvMetadata, setCsvMetadata] = useState(null);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState(disableRestaurantFilter ? defaultRestaurantId : "");
  const [mappingSelections, setMappingSelections] = useState({});
  const [preprocessing, setPreprocessing] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [importResult, setImportResult] = useState(null);

  const onRestaurantSearchChangeRef = React.useRef(onRestaurantSearchChange);
  useEffect(() => {
    onRestaurantSearchChangeRef.current = onRestaurantSearchChange;
  }, [onRestaurantSearchChange]);

  useEffect(() => {
    if (!open) {
      setStep(0);
      setSelectedFile(null);
      setProcessedFile(null);
      setCsvMetadata(null);
      setSelectedRestaurantId(disableRestaurantFilter ? defaultRestaurantId : "");
      setMappingSelections({});
      setPreprocessing(false);
      setUploadError("");
      setImportResult(null);
      onRestaurantSearchChangeRef.current("");
    }
  }, [open, disableRestaurantFilter, defaultRestaurantId]);

  useEffect(() => {
    if (open && disableRestaurantFilter && defaultRestaurantId) {
      setSelectedRestaurantId(defaultRestaurantId);
    }
  }, [open, disableRestaurantFilter, defaultRestaurantId]);

  const { rootRef: restaurantMenuRef, targetRef: restaurantLoaderRef } = useInfiniteScrollObserver({
    fetchNextPage: fetchNextRestaurantsPage,
    hasNextPage: hasNextRestaurantsPage,
    isFetchingNextPage: restaurantsFetchingNextPage,
  });

  const selectedRestaurantOption = useMemo(
    () => (
      restaurantOptionsCache.find((restaurant) => `${restaurant?.value ?? restaurant?.id}` === `${selectedRestaurantId}`)
      || restaurants.find((restaurant) => `${restaurant?.value}` === `${selectedRestaurantId}`)
    ),
    [restaurantOptionsCache, restaurants, selectedRestaurantId],
  );

  const filteredRestaurants = useMemo(
    () => (
      restaurantSearch.trim()
        ? restaurants.filter((restaurant) => matchesNormalizedSearch(restaurant?.label, restaurantSearch))
        : selectedRestaurantOption
          ? mergeUniqueByKey(restaurants, [selectedRestaurantOption], "value")
          : restaurants
    ),
    [restaurantSearch, restaurants, selectedRestaurantOption],
  );

  const hasRequiredMappings = useMemo(() => {
    const selectedValues = Object.values(mappingSelections).filter((value) => value && value !== "ignore");
    return REQUIRED_FIELDS.every((field) => selectedValues.includes(field));
  }, [mappingSelections]);

  const handleClose = () => {
    if (isUploading) {
      return;
    }

    onClose();
  };

  const handleSelectFile = async (file) => {
    if (!file?.name?.toLowerCase().endsWith(".csv")) {
      setSelectedFile(null);
      setProcessedFile(null);
      setCsvMetadata(null);
      setMappingSelections({});
      setUploadError("Only CSV files are supported");
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setProcessedFile(null);
      setCsvMetadata(null);
      setMappingSelections({});
      setUploadError("Uploaded csv file is more than 5MB");
      return;
    }

    setSelectedFile(file);
    setProcessedFile(null);
    setCsvMetadata(null);
    setMappingSelections({});
    setImportResult(null);
    setUploadError("");
    setPreprocessing(true);

    try {
      const metadata = await preprocessCsvFile(file);
      setProcessedFile(metadata.processedFile);
      setCsvMetadata(metadata);
      setMappingSelections(buildInitialMappings(metadata.headers));
    } catch (error) {
      setSelectedFile(null);
      setProcessedFile(null);
      setCsvMetadata(null);
      setMappingSelections({});
      setUploadError(error?.message || "Failed to process CSV file");
    } finally {
      setPreprocessing(false);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    setProcessedFile(null);
    setCsvMetadata(null);
    setMappingSelections({});
    setUploadError("");
  };

  const handleDownloadTemplate = () => {
    const downloadLink = document.createElement("a");
    const sampleCsvPath = `${process.env.PUBLIC_URL}/templates/employee_csv_import_template.csv`;

    downloadLink.href = sampleCsvPath;
    downloadLink.download = "employee_csv_import_template.csv";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const handleMappingChange = (headerKey, value) => {
    setMappingSelections((prev) => ({
      ...prev,
      [headerKey]: value,
    }));
  };

  const handleImport = async () => {
    if (!processedFile || !selectedRestaurantId || !csvMetadata) {
      return;
    }

    const headerMapping = csvMetadata.headers.reduce((accumulator, header) => {
      const selectedValue = mappingSelections[header.normalizedHeader];

      if (selectedValue && selectedValue !== "ignore") {
        accumulator[header.normalizedHeader] = selectedValue;
      }

      return accumulator;
    }, {});

    try {
      const result = await onImport({
        file: processedFile,
        restaurantId: selectedRestaurantId,
        headerMapping,
      });
      setImportResult(result || null);
      setStep(2);
    } catch (_error) {
      // Snackbar handling is owned by the parent mutation layer.
    }
  };

  const renderStepBody = () => {
    if (step === 0) {
      return (
        <UploadStep
          selectedFile={selectedFile}
          onSelectFile={handleSelectFile}
          onClearFile={handleClearFile}
          uploadError={uploadError}
          onDownloadTemplate={handleDownloadTemplate}
        />
      );
    }

    if (step === 1) {
      return (
        <ColumnMappingStep
          fileName={csvMetadata?.fileName || selectedFile?.name || ""}
          rowCount={csvMetadata?.rowCount || 0}
          columnCount={csvMetadata?.columnCount || 0}
          headers={csvMetadata?.headers || []}
          mappings={mappingSelections}
          onMappingChange={handleMappingChange}
          fieldOptions={FIELD_OPTIONS}
          sampleValues={SAMPLE_VALUES}
        />
      );
    }

    return <ImportSummaryStep result={importResult} />;
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalContainer data-testid="manual-csv-employee-import-modal">
        <HeaderContainer>
          <HeaderTopRow>
            <div>
              <TitleText>Import Mobile App Users</TitleText>
              <SubtitleText>
                Upload a CSV file. The system extracts only known fields and ignores the rest.
              </SubtitleText>
            </div>
            <StyledCloseButton data-testid="manual-csv-import-close" onClick={handleClose} disabled={isUploading}>
              <CloseIcon />
            </StyledCloseButton>
          </HeaderTopRow>

          <StepperContainer>
            {STEP_LABELS.map((label, index) => (
              <React.Fragment key={label}>
                <StepItem>
                  <StepCircle active={step === index} completed={step > index ? 1 : 0}>
                    {index + 1}
                  </StepCircle>
                  <StepLabel active={step === index ? 1 : 0}>{label}</StepLabel>
                </StepItem>
                {index < STEP_LABELS.length - 1 ? <StepConnector /> : null}
              </React.Fragment>
            ))}
          </StepperContainer>
        </HeaderContainer>

        <BodyContainer>
          {renderStepBody()}
        </BodyContainer>

        <FooterDivider />
        <FooterContainer>
          {step === 0 ? (
            <>
              <FooterLeft>
                <StyledSelect
                  data-testid="manual-csv-import-restaurant-select"
                  fullWidth
                  value={selectedRestaurantId}
                  displayEmpty
                  SelectDisplayProps={{
                    "data-testid": "manual-csv-import-restaurant-select-trigger",
                  }}
                  onChange={(event) => setSelectedRestaurantId(event.target.value)}
                  onClose={() => setTimeout(() => onRestaurantSearchChange(""), 300)}
                  disabled={isUploading || disableRestaurantFilter}
                  renderValue={(value) => {
                    if (!value) {
                      return <span style={{ color: "#9ca3af" }}>Select restaurant</span>;
                    }

                    return selectedRestaurantOption?.label || restaurants.find((restaurant) => restaurant.value === value)?.label || "";
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: { maxHeight: 400 },
                      ref: restaurantMenuRef,
                    },
                    MenuListProps: { sx: { pt: 0 } },
                  }}
                >
                  <DropdownSearchBar changeSearchText={onRestaurantSearchChange} placeholder="Search restaurant" isFetching={createRestaurantFetching} />
                  {filteredRestaurants?.map((restaurant) => (
                    <StyledMenuItem key={restaurant.value} value={restaurant.value} data-testid={`manual-csv-import-restaurant-option-${restaurant.value}`}>
                      {selectedRestaurantId === restaurant.value ? (
                        <SelectedMenuItem data={restaurant.label} />
                      ) : (
                        restaurant.label
                      )}
                    </StyledMenuItem>
                  ))}
                  <DropdownInfiniteLoader
                    hasNextPage={hasNextRestaurantsPage}
                    targetRef={restaurantLoaderRef}
                  />
                </StyledSelect>
              </FooterLeft>

              <FooterActions>
                <ModalCancelButton onClick={handleClose} disabled={isUploading}>
                  Cancel
                </ModalCancelButton>
                <PrimaryButton
                  data-testid="manual-csv-import-next"
                  variant="contained"
                  onClick={() => setStep(1)}
                  disabled={!selectedRestaurantId || !selectedFile || !processedFile || preprocessing || Boolean(uploadError)}
                  endIcon={preprocessing ? null : <ArrowForwardRoundedIcon />}
                >
                  {preprocessing ? <LoadingSpinner isLoading={true} /> : "Next"}
                </PrimaryButton>
              </FooterActions>
            </>
          ) : null}

          {step === 1 ? (
            <>
              <div />
              <FooterActions>
                <SecondaryButton data-testid="manual-csv-import-back" variant="outlined" onClick={() => setStep(0)} startIcon={<ArrowBackRoundedIcon />}>
                  Back
                </SecondaryButton>
                <PrimaryButton
                  data-testid="manual-csv-import-submit"
                  variant="contained"
                  onClick={handleImport}
                  disabled={!hasRequiredMappings || isUploading}
                >
                  {isUploading ? <LoadingSpinner isLoading={true} /> : "Import"}
                </PrimaryButton>
              </FooterActions>
            </>
          ) : null}

          {step === 2 ? (
            <>
              <div />
              <FooterActions>
                <PrimaryButton 
                  data-testid="manual-csv-import-finish"
                  variant="contained" 
                  onClick={handleClose}
                >
                  Close
                </PrimaryButton>
              </FooterActions>
            </>
          ) : null}
        </FooterContainer>
      </ModalContainer>
    </Modal>
  );
}
